import { useEffect, useState } from "react";
import { TIME_UNITS, IMPERIAL_UNITS, METRIC_UNITS } from "constants/estimatingDatabasePages";
import { isMetricTemplate } from "helpers/estimatingDatabase/databaseUnits";
import { databaseSelect } from "helpers/estimatingDatabase/databaseRows";

const asOptions = (list) => list.map((v) => ({ value: v, label: v }));
const str = (v) => (v === undefined || v === null ? "" : String(v));

// The record the estimating app would use for a lookup list
// (EstimationResponse.CompanySelect: company, else average, else default).
export const companySelect = (row) => (row ? row.company || row.average || row.default || null : null);

export function unitOptions(kind, template, extra = []) {
  const units = isMetricTemplate(template) ? METRIC_UNITS : IMPERIAL_UNITS;
  let list = [];
  if (kind === "time") list = TIME_UNITS;
  else if (kind === "units") list = units;
  else if (kind === "unitsAndTime") list = [...units, ...TIME_UNITS];
  const all = [...list, ...extra.filter((u) => u && !list.includes(u))];
  return asOptions(all);
}

// The desktop's reference lists (AwpUtil, ClarificationTypeUtil,
// MaterialClassUtil, ProjectTypesUtil, LaborTypesUtil) are ServiceUtil
// instances built with combineItems = true:
//   - fetched with the OrganizationId ONLY (ServiceUtil.GetData calls
//     service.GetAsync(organizationId) - no Country/State/City/ProjectType/
//     LaborType/Version), so they span every version of the company and
//     BidLight's defaults;
//   - one record per row picked by EstimationResponse.DatabaseSelect()
//     (average, else default, else company);
//   - refetched when the organisation changes.
// `refreshKey` re-reads the list after the page saved into it (the desktop
// calls IAwpUtil.Reset() after saving AWPs).
export function useLookup(workspace, endpoint, { enabled = true, refreshKey } = {}) {
  const [rows, setRows] = useState([]);
  const { fetchOrgRows, orgId } = workspace;
  useEffect(() => {
    let cancelled = false;
    if (!enabled || !endpoint || !orgId || !fetchOrgRows) {
      setRows([]);
      return undefined;
    }
    // Never show the previous organisation's list while the new one loads.
    setRows([]);
    fetchOrgRows(endpoint)
      .then((r) => !cancelled && setRows((r || []).map(databaseSelect).filter(Boolean)))
      .catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
  }, [enabled, endpoint, orgId, fetchOrgRows, refreshKey]);
  return rows;
}

// AwpViewModel.ToString(): "<Awp> <Description>".
export const awpLabel = (a) => (a ? `${str(a.Awp)} ${str(a.Description)}`.trim() : "");

export const awpOptions = (awps) =>
  (awps || []).filter((a) => a.Awp).map((a) => ({ value: a.Awp, label: awpLabel(a) }));

// A single AWP value shown the way the desktop's AWP combo shows it
// (SelectedValuePath="Awp" over AwpUtil items): "<Awp> <Description>", the
// raw code when the list has no such AWP.
export function awpDisplay(awps, value) {
  if (value === undefined || value === null || value === "") return "";
  const hit = (awps || []).find((a) => a.Awp === value);
  return hit ? awpLabel(hit) : String(value);
}

// ClarificationsDatabaseViewModel.ResponseToCompany: the subject is the part
// of Group before the first ".", matched on the clarification type's Code
// in ClarificationTypeUtil.Items; the combo shows its Description.
export const clarificationSubjectCode = (group) => str(group).split(".")[0];

export function clarificationSubject(types, group) {
  const code = clarificationSubjectCode(group);
  return (types || []).find((t) => str(t.Code) === code) || null;
}

export const clarificationSubjectOptions = (types) =>
  (types || [])
    .filter((t) => t.Code !== undefined && t.Code !== null && t.Code !== "")
    .map((t) => ({ value: str(t.Code), label: t.Description || str(t.Code) }));

// DatabaseTabViewModel.Version setter: the version stores the project /
// labour type's APIUrlIdentifier and the bar shows the matching item's
// DisplayName (= Category). No match leaves the field empty.
export function typeDisplayName(types, apiUrlIdentifier) {
  if (apiUrlIdentifier === undefined || apiUrlIdentifier === null || apiUrlIdentifier === "") return null;
  const hit = (types || []).find((t) => t.APIUrlIdentifier === apiUrlIdentifier);
  return hit ? hit.Category || null : null;
}

// Country / State / City of a version: Countries matched on Iso3, the
// country's States on StateCode, the state's Cities on Name; the bar shows
// each one's Name (DatabaseTabViewModel.Version setter + BreadCrumb).
export function locationNames(countries, template) {
  const t = template || {};
  const country = (countries || []).find((c) => c && c.iso3 === t.Country) || null;
  const state = country ? (country.states || []).find((s) => s && s.state_code === t.State) || null : null;
  const city = state ? (state.cities || []).find((c) => c && c.name === t.City) || null : null;
  return {
    country: country ? country.name : null,
    state: state ? state.name : null,
    city: city ? city.name : null,
  };
}

// MaterialFormulaStore.GetMaterialFormulas: the version's formulas, one per
// row as Company ?? Default (fallback rows included).
export const formulaChoices = (rawRows) =>
  (rawRows || []).map((r) => (r ? r.company || r.default || null : null)).filter((f) => f && f.Code);

// MaterialFormula: the stored config's formula is found again by Code AND
// Formula text, so an edited formula of the same name is not taken for it.
export function findFormula(formulas, name, formula) {
  const list = formulas || [];
  return (
    list.find((f) => f.Code === name && f.Formula === formula) || null
  );
}

// A catalog division code shown with its description, from the version's
// catalog rows (CompanySelect). The raw code when the version has no such
// division.
export function catalogDivisionDisplay(catalogRows, code) {
  if (code === undefined || code === null || code === "") return "";
  const hit = (catalogRows || []).map(companySelect).find((c) => c && c.Code === code);
  return hit && hit.Description ? `${code} ${hit.Description}` : String(code);
}
